import { useState, useEffect, useCallback } from 'react';
import * as authApi from '@/features/auth/auth.api';
import { useAuthStore } from '@/stores/authStore';

type Theme = 'light' | 'dark';

const STORAGE_KEY = 'xxtract-theme';

function getInitialTheme(): Theme {
  const saved = localStorage.getItem(STORAGE_KEY);
  if (saved === 'light' || saved === 'dark') return saved;
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function applyTheme(theme: Theme) {
  document.documentElement.setAttribute('data-theme', theme);
}

export function useTheme() {
  const [theme, setThemeState] = useState<Theme>(getInitialTheme);
  const [isSystemTheme, setIsSystemTheme] = useState<boolean>(
    () => localStorage.getItem(STORAGE_KEY) === null,
  );
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  const setTheme = useCallback(
    (newTheme: Theme) => {
      setIsSystemTheme(false);
      setThemeState(newTheme);
      applyTheme(newTheme);
      localStorage.setItem(STORAGE_KEY, newTheme);
      if (isAuthenticated) {
        authApi.updatePreferences({ theme: newTheme }).catch(() => {});
      }
    },
    [isAuthenticated],
  );

  // Follows the OS-level preference instead of a manual override. The backend only
  // accepts 'light' | 'dark' for the preference, so this stays a client-only
  // (localStorage) state — same storage key/mechanism as setTheme, just cleared.
  const followSystem = useCallback(() => {
    setIsSystemTheme(true);
    localStorage.removeItem(STORAGE_KEY);
    const systemTheme: Theme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    setThemeState(systemTheme);
    applyTheme(systemTheme);
  }, []);

  const toggle = useCallback(() => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  }, [theme, setTheme]);

  // Listen for system preference changes (only if no manual override)
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => {
      if (!localStorage.getItem(STORAGE_KEY)) {
        const newTheme = e.matches ? 'dark' : 'light';
        setThemeState(newTheme);
        applyTheme(newTheme);
      }
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Apply on mount
  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  return { theme, setTheme, toggle, isDark: theme === 'dark', isSystemTheme, followSystem };
}
